import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import AssetsApi from '../../services/api/AssetsApi';
import {
  AssetsOperationsType,
  AssetsStatusType,
  AssetsType,
  LoansStatusType,
  TabType,
} from '../../types';
import { RootState } from '../../types';
import { showError } from '../../utils/utils';
import { addHours } from 'date-fns';

import { ASSETS_STATUS } from '../../constants';

const setAssetsLoading = createAction<{ type: 'info'; value: boolean }>(
  'assets/setLoading'
);

const fetchAssets = createAsyncThunk(
  'assets/fetchAssets',
  async (
    payload: {
      status?: ASSETS_STATUS;
      type: AssetsType;
      dateFrom?: Date;
      dateTo?: Date;
      issued?: boolean;
      forIssuer?: boolean;
      search?: string;
    },
    thunkAPI
  ) => {
    const {
      status,
      search,
      type,
      dateFrom,
      dateTo,
      forIssuer = false,
      issued = true,
    } = payload;

    const filters = {} as {
      status?: ASSETS_STATUS;
      dateFrom: string;
      dateTo: string;
      forIssuer: boolean;
      search?: string;
    };

    filters.status = status;
    filters.search = search;
    if (dateFrom && dateTo) {
      filters.dateFrom = dateFrom ? addHours(dateFrom, 3).toISOString() : '';
      filters.dateTo = dateTo ? addHours(dateTo, 3).toISOString() : '';
    }

    try {
      thunkAPI.dispatch(setAssetsLoading({ type: 'info', value: true }));
      const store = thunkAPI.getState() as RootState;
      const response = !issued
        ? await AssetsApi.getNonIssuedAssets()
        : await AssetsApi.getAssets({
            token: store.auth.account.token,
            id: store.auth.account.id,
            type: type,
            issuer: forIssuer,
            ...filters,
          });
      if (response.status === 200) {
        // callback && callback(response.data);
        return response.data;
      }
      return undefined;
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(setAssetsLoading({ type: 'info', value: false }));
    }
  }
);

const fetchOperationConfirmations = createAsyncThunk(
  'assets/fetchOperations',
  async (
    payload: {
      type: AssetsOperationsType;
    },
    thunkAPI
  ) => {
    const { type } = payload;
    try {
      thunkAPI.dispatch(setAssetsLoading({ type: 'info', value: true }));
      const response = await AssetsApi.getOperationForConfirmations({
        type: type,
      });
      if (response.status === 200) {
        return response.data;
      }
      return undefined;
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(setAssetsLoading({ type: 'info', value: false }));
    }
  }
);

const confirmIssue = createAsyncThunk(
  'assets/confirmIssue',
  async (
    payload: {
      decisionId: number;
    },
    thunkAPI
  ) => {
    const { decisionId } = payload;
    const store = thunkAPI.getState() as RootState;
    const response = await AssetsApi.confirmIssueByDecisionId({
      token: store.auth.account.token,
      decId: decisionId,
    });
    if (response.status === 200) {
      return response.data;
    }
    return undefined;
  }
);

const confirmOperation = createAsyncThunk(
  'assets/confirmIssue',
  async (payload: { operationId: number; type: string }) => {
    const { operationId, type } = payload;
    const response = await AssetsApi.confirmOperation({
      type: type,
      operationId: operationId,
    });
    if (response.status === 200) {
      return response.data;
    }
    return undefined;
  }
);

const fetchLoans = createAsyncThunk(
  'assets/fetchLoans',
  async (
    payload: {
      status?: LoansStatusType;
      dateFrom?: string;
      dateTo?: string;
    },
    thunkAPI
  ) => {
    const { status, dateFrom, dateTo } = payload;
    const filters = {} as {
      status: LoansStatusType;
      dateFrom: string;
      dateTo: string;
    };
    if (status) {
      filters.status = status;
    }
    if (dateFrom && dateTo) {
      filters.dateFrom = dateFrom;
      filters.dateTo = dateTo;
    }
    try {
      thunkAPI.dispatch(setAssetsLoading({ type: 'info', value: true }));
      const store = thunkAPI.getState() as RootState;
      const response = await AssetsApi.getLoans({
        token: store.auth.account.token,
        id: store.auth.account.id,
        ...filters,
      });
      if (response.status === 200) {
        return response.data;
      }
      return undefined;
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(setAssetsLoading({ type: 'info', value: false }));
    }
  }
);

const setActiveTabType = createAction<{ value: TabType }>(
  'assets/setActiveTabType'
);

const setSearchStatus = createAction<{ value: AssetsStatusType }>(
  'assets/setSearchStatus'
);

export {
  fetchAssets,
  confirmOperation,
  fetchOperationConfirmations,
  confirmIssue,
  setAssetsLoading,
  setActiveTabType,
  setSearchStatus,
  fetchLoans,
};
